import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import { Pagination } from 'dashboard/components/table';
import { useLoading } from 'ui/contexts/overlay/Loading';
import useToastContext from 'ui/hooks/useToast';
import {
  APPORTIONMENT_CYCLE_CONSUMER_UNITS_QUERY,
  ApportionmentCycleConsumerUnitsList,
} from 'apportionmentCycles/graphql/apportionmentCycleConsumerUnitsQuery';
import { ApportionmentCycleConsumerUnit } from 'apportionmentCycles/models/apportionmentCycleConsumerUnit';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { INITIAL_QUERY_STATE_CONFIG } from 'graphql/apollo/config';
import ApportionmentCycleConsumerUnitsTable from 'apportionmentCycles/components/table/ApportionmentCycleConsumerUnitTable';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { SubmitHandler, useForm } from 'react-hook-form';
import CustomApportionmentCycleConsumerUnitFormFilters, {
  FormFilters,
} from 'customApportionmentCycleConsumerUnit/components/table/CustomApportionmentCycleConsumerUnitFormFilters';
import {
  CooperativeMembersSelectList,
  COOPERATIVE_MEMBERS_SELECT_QUERY,
  COOPERATIVE_MEMBERS_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativeMembersSelectQuery';
import { useCSVDownloader } from 'react-papaparse';
import CsvButton from 'apportionmentCycles/components/apportionmentCycleConsumerUnits/CsvButton';
import { MdCloudDownload } from 'react-icons/md';

type ListState = {
  apportionmentCycleConsumerUnits: ApportionmentCycleConsumerUnit[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type ApportionmentCycleConsumerUnitPortuguese = {
  id: number;
  Rateio: string;
  'Mês referente': Date;
  'Unidade consumidora': string;
  Cooperado: string;
  'Unidade Geradora': string;
};

type FilterOptions = {
  generationUnitId: number;
  apportionmentCycleConsumerUnitReferenceMonth: Date;
};

const DASHBOARD_APPORTIONMENTS_CYCLE_ROUTE =
  '/dashboard/billing-cycle/apportionment-cycle';

const DASHBOARD_CREDIT_DISTRIBUTION_ROUTE =
  '/dashboard/billing-cycle/apportionment-cycle/credit-distribution';

const BREADCRUMB_PAGES: Page[] = [
  {
    name: 'Rateios',
    route: DASHBOARD_APPORTIONMENTS_CYCLE_ROUTE,
    current: false,
  },
  {
    name: 'Distribuição de créditos',
    route: DASHBOARD_CREDIT_DISTRIBUTION_ROUTE,
    current: true,
  },
];

const LIST_ERROR_TOAST: ToastProps = {
  title: 'Algo deu errado!',
  variant: 'danger',
  text: 'Não foi possível carregar a lista de Distribuição de Créditos',
};

export default function ApportionmentCycleConsumerUnitsPage({ ...props }) {
  const { addToast } = useToastContext();
  const { CSVDownloader, Type } = useCSVDownloader();
  const { LoadingOverlay, showLoading, closeLoading } = useLoading();

  const [filterOptions] = useState<FilterOptions>(
    props?.location?.state as FilterOptions
  );
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFilters>();

  const [listState, setListState] = useState<ListState>({
    apportionmentCycleConsumerUnits: [],
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [stateExport, setStateExport] = useState<ListState>({
    apportionmentCycleConsumerUnits: [],
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [
    apportionmentCycleConsumerUnitListExport,
    { data: dataExport, loading: loadingExport, error: errorExport },
  ] = useLazyQuery(APPORTIONMENT_CYCLE_CONSUMER_UNITS_QUERY, {
    variables: {
      pageSize: 5000,
      before: null,
      after: null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [
    cooperativeMembersSelect,
    {
      data: cooperativeMembers,
      loading: loadingCooperativeMembers,
      refetch: refetchCooperativeMembers,
    },
  ] = useLazyQuery<CooperativeMembersSelectList>(
    COOPERATIVE_MEMBERS_SELECT_QUERY,
    COOPERATIVE_MEMBERS_SELECT_QUERY_VARIABLES
  );

  const [apportionmentCycleConsumerUnitsList, { data, loading, error, refetch }] =
    useLazyQuery<ApportionmentCycleConsumerUnitsList>(
      APPORTIONMENT_CYCLE_CONSUMER_UNITS_QUERY,
      INITIAL_QUERY_STATE_CONFIG
    );

  useEffect(() => {
    apportionmentCycleConsumerUnitsList();
    apportionmentCycleConsumerUnitListExport();
  }, [
    apportionmentCycleConsumerUnitsList,
    apportionmentCycleConsumerUnitListExport,
  ]);

  const isError = error || errorExport;

  useEffect(() => {
    if (isError) {
      addToast(LIST_ERROR_TOAST);
    }
  }, [addToast, isError]);

  const isLoading = loading || loadingCooperativeMembers;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }
    if (data) {
      setListState({
        apportionmentCycleConsumerUnits:
          data.apportionmentCycleConsumerUnits.entries,
        hasNextPage: !!data.apportionmentCycleConsumerUnits.afterCursor,
        hasPreviousPage: !!data.apportionmentCycleConsumerUnits.beforeCursor,
      });
    }

    closeLoading();
  }, [closeLoading, data, isLoading, showLoading]);

  useEffect(() => {
    if (loadingExport) {
      showLoading();
      return;
    }

    if (dataExport) {
      setStateExport({
        apportionmentCycleConsumerUnits:
          dataExport.apportionmentCycleConsumerUnits.entries,
        hasNextPage: !!dataExport.apportionmentCycleConsumerUnits.afterCursor,
        hasPreviousPage: !!dataExport.apportionmentCycleConsumerUnits.beforeCursor,
      });
    }

    closeLoading();
  }, [closeLoading, dataExport, loadingExport, showLoading]);

  const handleClickNext = () => {
    refetch &&
      refetch({
        after: data?.apportionmentCycleConsumerUnits.afterCursor,
        before: null,
      });
  };

  const handleClickBefore = () => {
    refetch &&
      refetch({
        after: null,
        before: data?.apportionmentCycleConsumerUnits.beforeCursor,
      });
  };

  const apportionmentCycleConsumerUnitsPortuguese: ApportionmentCycleConsumerUnitPortuguese[] =
    [];

  stateExport.apportionmentCycleConsumerUnits.map(
    (item: ApportionmentCycleConsumerUnit) =>
      apportionmentCycleConsumerUnitsPortuguese.push({
        id: item.id,
        Rateio: item.apportionmentCycleConsumerUnitPercentual
          ?.toString()
          .replace('.', ','),
        'Mês referente': item.apportionmentCycleConsumerUnitReferenceMonth,
        'Unidade consumidora':
          item.consumerUnit.consumerUnitPowerDistributionUnitIdentifier,
        Cooperado: item.consumerUnit.cooperativeMember.cooperativeMemberLegalName,
        'Unidade Geradora': item.generationUnit.generationUnitLegalName,
      })
  );

  useEffect(() => {
    apportionmentCycleConsumerUnitsList();
  }, [apportionmentCycleConsumerUnitsList]);

  useEffect(() => {
    filterOptions &&
      refetch({
        filters: {
          generationUnitId: Number(filterOptions.generationUnitId),
          apportionmentCycleConsumerUnitReferenceMonth:
            filterOptions.apportionmentCycleConsumerUnitReferenceMonth,
        },
      });
  }, [filterOptions, refetch]);

  const onSubmit: SubmitHandler<FormFilters> = (apportionmentCycleConsumerUnit) => {
    refetch({
      filters: Object.fromEntries(
        Object.entries(apportionmentCycleConsumerUnit).filter(([, value]) => !!value)
      ),
    });
  };

  useEffect(() => {
    refetchCooperativeMembers && refetchCooperativeMembers();

    cooperativeMembersSelect();
  }, [cooperativeMembersSelect, refetchCooperativeMembers]);

  return (
    <Dashboard
      dashboardMainHeaderTitle={
        <DashboardMainHeaderForm
          title="Distribuição de créditos"
          breadcrumbPages={BREADCRUMB_PAGES}
        >
          <></>
        </DashboardMainHeaderForm>
      }
    >
      <div className="flex w-full justify-between">
        <form onSubmit={handleSubmit(onSubmit)} className="flex w-full">
          <CustomApportionmentCycleConsumerUnitFormFilters
            errors={errors}
            control={control}
            register={register}
            isLoading={isLoading}
            cooperativeMembers={cooperativeMembers?.cooperativeMembers?.entries}
          />
        </form>
        <div className="flex items-end justify-end pb-6">
          <CSVDownloader
            bom={true}
            type={Type.Link}
            config={{
              delimiter: ';',
            }}
            filename={'apportionments'}
            data={apportionmentCycleConsumerUnitsPortuguese}
          >
            <CsvButton variant={'secondaryGray'} size="sm" className="justify-end">
              <MdCloudDownload size={20} /> CSV
            </CsvButton>
          </CSVDownloader>
        </div>
      </div>

      <div className="rounded-lg bg-gray-background">
        {listState && (
          <>
            <ApportionmentCycleConsumerUnitsTable
              apportionmentCycleConsumerUnits={
                listState.apportionmentCycleConsumerUnits
              }
            />
            <Pagination
              onNextClick={handleClickNext}
              onPreviousClick={handleClickBefore}
              disableNext={!listState.hasNextPage || isLoading}
              disableBefore={!listState.hasPreviousPage || isLoading}
            />
          </>
        )}
      </div>
      <LoadingOverlay />
    </Dashboard>
  );
}
